import {
  ascend,
  clone,
  compose,
  filter,
  head,
  identity,
  ifElse,
  includes,
  insertAll,
  map,
  lensPath,
  path,
  pipe,
  prop,
  reduce,
  reject,
  set,
  sort,
  view,
} from "ramda";
import { isBlank, notBlank } from "../../../util/ext";

//-- For Edit or Upload
export const ADMIN_ONLY_FIELDS = ["internalPublisherId"];

//-- Optional Fields for ContentEditDialog
export const optionalContentFields = [
  {
    fieldName: ["ageRange"],
    label: "Age Range",
  },
  {
    fieldName: ["attribution"],
    label: "Attribution",
  },
  {
    fieldName: ["contentProviderCollection"],
    label: "Content Provider Collection",
  },
  {
    fieldName: ["funder"],
    label: "Funder",
  },
  {
    fieldName: ["internalPublisherId"],
    label: "Internal Publisher ID",
  },
  {
    fieldName: ["issueNumber"],
    label: "Issue #",
  },
  {
    fieldName: ["keywords"],
    label: "Keywords",
    size: 6,
  },
  {
    fieldName: ["lexileCode"],
    label: "Lexile Code",
    inputProps: { maxLength: 2 },
  },
  {
    fieldName: ["lexileLevel"],
    label: "Lexile Level",
    inputProps: { type: "number" },
  },
  {
    fieldName: ["oclcControlNumber"],
    label: "OCLC Control Number",
    inputProps: { maxLength: 12 },
  },
  {
    fieldName: ["periodicalTitle"],
    label: "Periodical",
  },
  {
    fieldName: ["placeOfPublication"],
    label: "Place of Publication",
  },
  {
    fieldName: ["publisherName"],
    label: "Publisher",
  },
  {
    fieldName: ["purchaseLink", "title"],
    label: "Purchase Title",
  },
  {
    fieldName: ["purchaseLink", "url"],
    label: "Purchase URL",
  },
  {
    fieldName: ["rating"],
    label: "Rating",
  },
  {
    fieldName: ["series"],
    label: "Series",
  },
  {
    fieldName: ["sourceUrl"],
    label: "Source URL",
  },
  {
    fieldName: ["textDirectionality"],
    label: "Text Directionality",
  },
  {
    fieldName: ["volume"],
    label: "Volume",
  },
];

export const optionalBookFields = [
  {
    fieldName: ["edition"],
    label: "Edition",
  },
];

export const optionalArticleFields = [
  {
    fieldName: ["periodicalVolume"],
    label: "Periodical Volume",
  },
  {
    fieldName: ["pageRange"],
    label: "Page Range",
  },
];

export const optionalAudioFields = [
  {
    fieldName: ["episode"],
    label: "Episode",
  },
];

export const optionalVideoFields = [
  {
    fieldName: ["episode"],
    label: "Episode",
  },
];

export const optionalSubmissionFields = [
  { fieldName: ["audience"], label: "Audience" },
];

export const getFieldsToExcludeFromDropdown = type => {
  switch (type) {
    case "ALBUM":
    case "CHAPTER":
    case "OTHER_DOCUMENT":
    case "IMAGE":
      return ["publisherName"];
    case "ARTICLE":
      return ["volume", "volumeNumber", "pageRange", "publisherName"];
    case "AUDIOBOOK":
    case "BOOK":
      return ["volume", "edition", "issueNumber", "isbn", "publisherName"];
    case "VIDEO":
      return ["keywords", "series", "episode", "publisherName"];
    default:
      return [];
  }
};

export const byLabel = ascend(prop("label"));

// change empty strings ("") to null
export const formatOptionalFields = optionalFields =>
  reduce(
    (fields, field) => {
      const value = isBlank(field.value) ? null : field.value;
      return set(lensPath(field.fieldName), value, fields);
    },
    {},
    optionalFields,
  );

export const getDropdownList = content =>
  compose(
    sort(byLabel),
    reject(optionalField =>
      includes(
        head(optionalField.fieldName),
        getFieldsToExcludeFromDropdown(content.type),
      ),
    ),
    filter(optionalField => isBlank(path(optionalField.fieldName, content))),
  );

export const getOptionalFields = (optionalFields, content, isAdmin) =>
  compose(
    map(field => {
      let value;
      // check for content metadata saved by code in db, like rating/ratingCode
      if (field.label === "Rating" && content?.ratingCode) {
        value = content?.ratingCode;
      } else {
        value = view(lensPath(field.fieldName), content);
      }

      return {
        ...field,
        value: value,
      };
    }),
    reject(optionalField =>
      includes(
        head(optionalField.fieldName),
        getFieldsToExcludeFromDropdown(content.type),
      ),
    ),
    pipe(ifElse(() => isAdmin, identity, getNonAdminFields)),
    filter(optionalField => notBlank(path(optionalField.fieldName, content))),
  )(optionalFields);

export const getNonAdminFields = reject(f =>
  includes(head(f.fieldName), ADMIN_ONLY_FIELDS),
);

export const optionalContentFieldsMapper = contentType => {
  let optionalFieldsClone = clone(optionalContentFields);
  let additionalFields = [];
  switch (contentType) {
    case "ARTICLE":
      additionalFields = optionalArticleFields;
      break;
    case "AUDIO":
      additionalFields = optionalAudioFields;
      break;
    case "BOOK":
      additionalFields = optionalBookFields;
      break;
    case "VIDEO":
      additionalFields = optionalVideoFields;
      break;
    default:
      break;
  }
  return insertAll(0, additionalFields, optionalFieldsClone);
};
